"use client";

import { useState } from "react";

import { logEvent } from "@/services/analytics";
import { getKeyString } from "@/utils/common";

export interface McpVideoProps {
  // Poster image shown before the visitor clicks play. This is the LCP
  // candidate for the /mcp hero, so it renders as a plain eager <img>
  // (NOT next/image or HeroUI Image — see memory/project_heroui_image_lazy_bug).
  poster: string;
  label: string;
  fallbackLabel: string;
}

const VIDEO_ID = "z3SWam7_AiA";
const SHORT_URL = `https://www.youtube.com/shorts/${VIDEO_ID}`;
// youtube-nocookie = privacy-enhanced mode (no cookies until playback).
// The iframe is only injected after a click (facade pattern), so it never
// blocks the hero's LCP or ships YouTube's player JS on first paint.
// mute=1 → starts silent; the user un-mutes with the player's own control.
const EMBED_URL = `https://www.youtube-nocookie.com/embed/${VIDEO_ID}?autoplay=1&mute=1&rel=0`;

/**
 * Click-to-load YouTube facade for the /mcp hero. Before the click, only a
 * static poster + play button paint (LCP-safe, zero third-party JS). On click,
 * the poster is swapped for the autoplaying embed. A persistent "Watch on
 * YouTube" link is the graceful fallback if the embed is ever blocked.
 */
export function McpVideo({ poster, label, fallbackLabel }: McpVideoProps) {
  const [active, setActive] = useState(false);

  return (
    <div className="mx-auto w-full max-w-2xl">
      <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-black shadow-lg ring-1 ring-default-200">
        {active ? (
          <iframe
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
            className="absolute inset-0 h-full w-full"
            src={EMBED_URL}
            title={label}
          />
        ) : (
          <button
            aria-label={label}
            className="group absolute inset-0 h-full w-full cursor-pointer"
            type="button"
            onClick={() => {
              logEvent(getKeyString("mcp_video_play"));
              setActive(true);
            }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              alt={label}
              className="absolute inset-0 h-full w-full object-cover"
              decoding="async"
              fetchPriority="high"
              height={720}
              loading="eager"
              src={poster}
              width={1280}
            />
            <span className="absolute inset-0 bg-black/30 transition-colors group-hover:bg-black/20" />
            <span className="absolute left-1/2 top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-primary text-white shadow-lg transition-transform group-hover:scale-110 sm:h-10 sm:w-10">
              <svg
                aria-hidden="true"
                className="ml-0.5 h-4 w-4 sm:h-5 sm:w-5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </button>
        )}
      </div>
      <div className="mt-3 text-center">
        <a
          className="text-sm text-default-500 underline-offset-2 hover:text-primary hover:underline"
          href={SHORT_URL}
          rel="noopener noreferrer"
          target="_blank"
        >
          {fallbackLabel} ↗
        </a>
      </div>
    </div>
  );
}
